import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5ncnf3lpot0rgnb822g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 lg:gap-20">
          {/* Contact Information */}
          <div>
            <div className="text-[#2563EB] text-xs sm:text-sm font-bold uppercase tracking-wider mb-3 md:mb-4">
              • GET IN TOUCH
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-5 sm:mb-6 md:mb-8 leading-tight">
              Let's Bring Your<br />
              Vision to Life
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-6 sm:mb-8 md:mb-10 lg:mb-12 leading-relaxed">
              Ready to transform your space? Contact us today for a free consultation and quote. We're here to answer your questions and help you get started on your remodeling journey.
            </p>

            <div className="space-y-5 sm:space-y-6 md:space-y-8">
              <div>
                <div className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 md:mb-3">
                  ADDRESS
                </div>
                <p className="text-sm sm:text-base text-gray-700">801 Copenhagen Way</p>
                <p className="text-sm sm:text-base text-gray-700">Winter Garden, FL 34787</p>
              </div>

              <div>
                <div className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 md:mb-3">
                  PHONE
                </div>
                <p className="text-sm sm:text-base text-gray-700">+1 (321) 947-6724</p>
                <p className="text-xs sm:text-sm text-gray-500">Mon-Fri: 8AM - 5PM</p>
              </div>

              <div>
                <div className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 md:mb-3">
                  EMAIL
                </div>
                <p className="text-sm sm:text-base text-gray-700">bestcallrestor@gmail.com</p>
                <p className="text-xs sm:text-sm text-gray-500">We respond within 24 hours</p>
              </div>
            </div>

            <div className="mt-6 sm:mt-8 md:mt-10 lg:mt-12 flex gap-3 md:gap-4">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-11 h-11 md:w-12 md:h-12 border-2 border-gray-300 rounded-full flex items-center justify-center text-gray-600 hover:border-[#2563EB] hover:text-[#2563EB] transition-all duration-300 cursor-pointer">
                <i className="ri-facebook-fill text-lg md:text-xl"></i>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-11 h-11 md:w-12 md:h-12 border-2 border-gray-300 rounded-full flex items-center justify-center text-gray-600 hover:border-[#2563EB] hover:text-[#2563EB] transition-all duration-300 cursor-pointer">
                <i className="ri-instagram-line text-lg md:text-xl"></i>
              </a>
              <a href="https://houzz.com" target="_blank" rel="noopener noreferrer" className="w-11 h-11 md:w-12 md:h-12 border-2 border-gray-300 rounded-full flex items-center justify-center text-gray-600 hover:border-[#2563EB] hover:text-[#2563EB] transition-all duration-300 cursor-pointer">
                <i className="ri-home-smile-line text-lg md:text-xl"></i>
              </a>
              <a href="https://google.com" target="_blank" rel="noopener noreferrer" className="w-11 h-11 md:w-12 md:h-12 border-2 border-gray-300 rounded-full flex items-center justify-center text-gray-600 hover:border-[#2563EB] hover:text-[#2563EB] transition-all duration-300 cursor-pointer">
                <i className="ri-google-fill text-lg md:text-xl"></i>
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#F8F6F3] rounded-2xl md:rounded-3xl p-5 sm:p-7 md:p-10">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-5 sm:mb-6 md:mb-8">
              Send Us a Message
            </h3>
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 md:space-y-6">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none transition-all text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none transition-all text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="contact-phone" className="block text-sm font-semibold text-gray-900 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="contact-phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none transition-all text-sm"
                  placeholder="+1 (321) 000-0000"
                />
              </div>

              <div>
                <label htmlFor="contact-service" className="block text-sm font-semibold text-gray-900 mb-2">
                  Service Interested In *
                </label>
                <select
                  id="contact-service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="Bathroom Remodel">Bathroom Remodel</option>
                  <option value="Kitchen Remodel">Kitchen Remodel</option>
                  <option value="Interior Painting">Interior Painting</option>
                  <option value="Exterior Painting">Exterior Painting</option>
                  <option value="Flooring Installation">Flooring Installation</option>
                  <option value="Drywall Work">Drywall Work</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-semibold text-gray-900 mb-2">
                  Project Details *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none transition-all resize-none text-sm"
                  placeholder="Tell us about your project..."
                />
                <div className="text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm">
                  ✓ Thank you! We'll get back to you within 24 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm">
                  Something went wrong. Please try again or call us directly.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6 sm:px-8 py-3 sm:py-3.5 md:py-4 rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}